import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const { hydrateFromToken } = useAuth();
    const token = params.get("token") || "";

    const [pwd, setPwd] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [tokenMissing, setTokenMissing] = useState(false);

    useEffect(() => {
        if (!token) setTokenMissing(true);
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (pwd.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        if (pwd !== confirm) {
            toast.error("Passwords don't match");
            return;
        }
        setLoading(true);
        try {
            const { data } = await client.post("/auth/reset-password", {
                token,
                new_password: pwd,
            });
            // Auto-sign-in with the fresh JWT the endpoint returned
            if (data?.token && hydrateFromToken) {
                await hydrateFromToken(data.token, data.user);
            }
            toast.success("Password updated. You're signed in.");
            nav("/app", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't reset password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-background">
            <div className="w-full max-w-sm" data-testid="reset-password-page">
                <Link to="/" data-testid="brand-link" className="inline-flex items-center gap-2 mb-10">
                    <Logo className="h-14 w-auto" />
                </Link>

                {tokenMissing ? (
                    <div data-testid="reset-token-missing">
                        <div className="overline mb-3">// invalid link</div>
                        <h1 className="font-display text-3xl tracking-tight mb-4">This link isn't valid.</h1>
                        <p className="text-sm text-muted-foreground mb-8">
                            The reset link is missing its token. Start over from the forgot-password page.
                        </p>
                        <Link
                            to="/forgot-password"
                            data-testid="request-new-link"
                            className="text-primary hover:underline text-sm"
                        >
                            → Request a new link
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={submit} data-testid="reset-password-form">
                        <div className="overline mb-3">// pick a new password</div>
                        <h1 className="font-display text-3xl tracking-tight mb-3">Lock it in.</h1>
                        <p className="text-sm text-muted-foreground mb-8">
                            Choose a strong new password. You'll be signed in right after.
                        </p>

                        <label className="text-xs text-muted-foreground block mb-2">New password</label>
                        <input
                            data-testid="reset-password-input"
                            type="password"
                            required
                            minLength={6}
                            autoFocus
                            value={pwd}
                            onChange={(e) => setPwd(e.target.value)}
                            className="w-full bg-input border border-border px-3 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                            placeholder="••••••••"
                        />

                        <label className="text-xs text-muted-foreground block mt-5 mb-2">Confirm password</label>
                        <input
                            data-testid="reset-password-confirm-input"
                            type="password"
                            required
                            minLength={6}
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            className="w-full bg-input border border-border px-3 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                            placeholder="••••••••"
                        />

                        <button
                            data-testid="reset-password-submit-btn"
                            disabled={loading}
                            className="w-full mt-8 bg-primary text-primary-foreground py-3 font-medium hover:bg-primary/90 disabled:opacity-50"
                        >
                            {loading ? "Updating…" : "Set new password →"}
                        </button>

                        <div className="mt-6 text-xs text-muted-foreground">
                            <Link to="/login" data-testid="back-to-login-link" className="text-primary hover:underline">← Back to sign in</Link>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
